import { ActionReducerMapBuilder, AsyncThunk, ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Collection } from "./collection";
import { AppDispatch } from "./store";

export function addSimpleCollectionReducers<ThunkArg>(
    builder: ActionReducerMapBuilder<any>,
    thunk: AsyncThunk<any, ThunkArg, any>,
    collectionSelector: (state: any) => Collection,
    reducerIntercept?: (type: any, state: any, action: any) => void,
    refreshAction?: ActionCreatorWithPayload<any>,
    idSelector = (item: any) => item.id,
) {
    builder.addCase(thunk.pending, (state, action) => {
        const collection = collectionSelector(state);
        if (collection && collection.currentRequestId)
            return;
        if (collection) {
            collection.currentRequestId = action.meta.requestId;
        }
        reducerIntercept?.(thunk.pending, state, action);
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        const collection = collectionSelector(state);
        if (collection && collection.currentRequestId !== action.meta.requestId)
            return;
        if (collection) {
            collection.currentRequestId = undefined;
            const items = action.payload.data;
            let ids = items.map((item: any) => idSelector(item))
            collection.ids = (collection.ids ?? []).concat(ids);
            collection.lastFetched = Date.now();
        }
        reducerIntercept?.(thunk.fulfilled, state, action);
    })
    builder.addCase(thunk.rejected, (state, action) => {
        const collection = collectionSelector(state)
        if (collection && collection.currentRequestId !== action.meta.requestId)
            return
        if (collection) {
            collection.currentRequestId = undefined
        }
        reducerIntercept?.(thunk.rejected, state, action)
    })
    if (refreshAction) {
        builder.addCase(refreshAction, (state) => {
            const collection = collectionSelector(state)
            if (collection && collection.currentRequestId)
                return;
            if (collection) {
                collection.ids = [];
            }
        })
    }
}

export function useSimpleCollection({ selector, action }: { selector: (state: any) => Collection, action: any }, cacheExpiry = 60 * 1000) {
    const dispatch = useDispatch<AppDispatch>();
    const collection = useSelector(selector)
    const lastFetched = collection?.lastFetched;
    const [needRefresh, setNeedRefresh] = useState(!lastFetched || Date.now() - lastFetched > cacheExpiry);

    const refresh = () => {
        dispatch(action)
    }

    useEffect(() => {
        if (needRefresh) {
            dispatch(action)
            setNeedRefresh(false);
        }
    }, [needRefresh, selector, action, dispatch]);

    const { lastFetched: _, ...rest } = collection || {};

    return {
        ...rest,
        refresh
    };
}
